import {
  BODY_MAP_3D_CALIBRATION_MALE,
  BodyMap3dCalibration,
} from './body-map-3d.calibration';
import {
  BODY_MAP_VIEWBOX_HEIGHT,
  BODY_MAP_VIEWBOX_WIDTH,
  BodyMapRegion,
  BodyMapView,
} from './body-map.types';

export interface BodyMap3dHit {
  regionId: string;
  view: BodyMapView;
  catalogX: number;
  catalogY: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export class BodyMap3dProjector {
  resolveView(params: {
    x: number;
    y: number;
    z: number;
    nx: number;
    ny: number;
    nz: number;
    cameraZ: number;
  }): BodyMapView {
    if (Math.abs(params.nz) >= 0.15) {
      return params.nz >= 0 ? 'FRONT' : 'BACK';
    }
    if (Math.abs(params.cameraZ) >= 0.15) {
      return params.cameraZ >= 0 ? 'FRONT' : 'BACK';
    }
    return params.z >= 0 ? 'FRONT' : 'BACK';
  }

  projectToCatalog(
    view: BodyMapView,
    x: number,
    y: number,
    calibration: BodyMap3dCalibration = BODY_MAP_3D_CALIBRATION_MALE,
  ): { x: number; y: number } {
    const spanX =
      calibration.maxX - calibration.minX + calibration.padX * 2;
    const spanY =
      calibration.maxY - calibration.minY + calibration.padY * 2;

    const normalizedX = clamp(
      (x - calibration.minX + calibration.padX) / spanX,
      0,
      1,
    );
    const normalizedY = clamp(
      (calibration.maxY + calibration.padY - y) / spanY,
      0,
      1,
    );

    let catalogX =
      normalizedX * BODY_MAP_VIEWBOX_WIDTH + calibration.offsetX;
    const catalogY =
      normalizedY * BODY_MAP_VIEWBOX_HEIGHT + calibration.offsetY;

    if (view === 'BACK') {
      catalogX = BODY_MAP_VIEWBOX_WIDTH - catalogX;
    }

    return { x: catalogX, y: catalogY };
  }

  resolveHit(params: {
    regions: BodyMapRegion[];
    view: BodyMapView;
    x: number;
    y: number;
    z: number;
    nx: number;
    ny: number;
    nz: number;
    cameraZ: number;
    calibration?: BodyMap3dCalibration;
  }): BodyMap3dHit | null {
    const calibration = params.calibration ?? BODY_MAP_3D_CALIBRATION_MALE;
    const resolvedView = this.resolveView(params);
    const catalogPoint = this.projectToCatalog(
      resolvedView,
      params.x,
      params.y,
      calibration,
    );
    const visible = params.regions.filter(
      (region) => region.view === resolvedView,
    );

    for (let i = visible.length - 1; i >= 0; i -= 1) {
      const region = visible[i];
      if (pathContains(region.pathD, catalogPoint.x, catalogPoint.y)) {
        return {
          regionId: region.id,
          view: resolvedView,
          catalogX: catalogPoint.x,
          catalogY: catalogPoint.y,
        };
      }
    }

    return null;
  }

  hitTestRegion(params: {
    regions: BodyMapRegion[];
    view: BodyMapView;
    x: number;
    y: number;
    z: number;
    nx: number;
    ny: number;
    nz: number;
    cameraZ: number;
    calibration?: BodyMap3dCalibration;
  }): string | null {
    return this.resolveHit(params)?.regionId ?? null;
  }
}

let hitTestContext: CanvasRenderingContext2D | null | undefined;

function getHitTestContext(): CanvasRenderingContext2D | null {
  if (hitTestContext !== undefined) {
    return hitTestContext;
  }
  if (typeof document === 'undefined') {
    hitTestContext = null;
    return null;
  }
  const canvas = document.createElement('canvas');
  hitTestContext = canvas.getContext('2d');
  return hitTestContext;
}

export function pathContains(
  pathD: string,
  x: number,
  y: number,
): boolean {
  const ctx = getHitTestContext();
  if (!ctx || typeof Path2D === 'undefined') {
    return false;
  }
  return ctx.isPointInPath(new Path2D(pathD), x, y);
}
